import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { previewRunLaterPost } from "../../Action/runLaterApi";
import { getAuthTokenPayload } from "../../Utils/index";
import { getPresignedUrl, uploadFileToS3 } from "../../Action/s3UploadApi";

import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  IconButton,
  Avatar,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormHelperText,
  Backdrop,
  Fade,
  LinearProgress,
  Zoom,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  AccountCircle as AccountIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as UploadIcon,
  Share as ShareIcon,
  RestartAlt as ResetIcon,
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import OruclicksAIIcon from "../../Components/AIChat/OruclicksAIIcon";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { postTemplates } from "../../MasterData/postTemplate.js";
import { loadSocialMediaAccounts, postToSocialMedia, fetchMonthlyPostCount, incrementMonthlyPostCount } from "../../Action/api";
import AIChat from "../../Components/AIChat";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import CalendarView from "../../Components/CalendarView";
import PreviewPostDialog from "../../Components/PreviewPostDialog";
import InstagramImageCropper from "../../Components/InstagramImageCropper";
import TemplateModal from "./components/TemplateModal";
import RunNow from "./components/RunNow";
import RunLater from "./components/RunLater";

// Back up Code 17 Oct 2025 (Very Important for Phase2)
// const platformConfigurations = {
//   "Content Only": ["Facebook", "Twitter (X)", "LinkedIn"],
//   Image: ["Facebook", "Instagram", "Twitter (X)", "LinkedIn"],
//   Video: ["Facebook", "Instagram", "Twitter (X)", "YouTube", "LinkedIn"],
// };

// Added on 17th Oct 2025 to remove Twitter and YouTube
// Updated: Video only supports LinkedIn (max 4MB)
const platformConfigurations = {
  "Content Only": ["Facebook"],
  Image: ["Facebook", "Instagram", "LinkedIn"],
  Video: ["LinkedIn"],
};

// Get available platforms based on content type
const getAvailablePlatforms = (contentType) => {
  if (!contentType) return [];
  return platformConfigurations[contentType] || [];
};

// Helper function to safely get file URL
const getFileURL = (file) => {
  if (!file) return "";
  
  // If file is already a string URL, return it
  if (typeof file === "string") {
    return file;
  }
  
  // If file is a File or Blob object, create object URL
  if (file instanceof File || file instanceof Blob) {
    try {
      return URL.createObjectURL(file);
    } catch (error) {
      console.error("Error creating object URL:", error);
      return "";
    }
  }
  
  // Fallback: return empty string
  console.warn("Invalid file type passed to getFileURL:", file);
  return "";
};

export const RunPost = () => {
  // Derive Run Later permission and monthly post limit from JWT menuList (MENU-002)
  const { canRunLater, allowPerMonthPost } = (() => {
    const menuList = getAuthTokenPayload()?.menuList ?? [];
    const runPostMenu = menuList.find((m) => m.menuId === "MENU-002");
    return {
      canRunLater: runPostMenu?.runLater === "yes",
      allowPerMonthPost: runPostMenu?.allowPerMonthPost ?? null,
    };
  })();

  // AI Assistant is exclusive to the Business plan
  const planId = getAuthTokenPayload()?.planId;
  const isBusinessPlan = planId === "oruclicks-bus-plan-003";

  const [tab, setTab] = useState(0);

  // Add these new states after your existing state declarations
  const [instagramCropperOpen, setInstagramCropperOpen] = useState(false);
  const [currentCroppingImage, setCurrentCroppingImage] = useState(null);
  const [currentCroppingIndex, setCurrentCroppingIndex] = useState(null);
  const [pendingImages, setPendingImages] = useState([]);

  // Social Media Accounts states
  const [socialMediaAccounts, setSocialMediaAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState("");

  // Run Now states - REMOVED runNowHashtag
  const [runNowPlatforms, setRunNowPlatforms] = useState([]);
  const [runNowContentType, setRunNowContentType] = useState("");
  const [runNowMediaFile, setRunNowMediaFile] = useState(null);
  const [runNowPostContent, setRunNowPostContent] = useState("");
  const [runNowSelectedAccounts, setRunNowSelectedAccounts] = useState([]);

  // Run Later states - REMOVED runLaterHashtag
  const [runLaterPlatforms, setRunLaterPlatforms] = useState([]);
  const [runLaterContentType, setRunLaterContentType] = useState("");
  const [runLaterMediaFile, setRunLaterMediaFile] = useState(null);
  const [runLaterPostContent, setRunLaterPostContent] = useState("");
  const [runLaterDateTime, setRunLaterDateTime] = useState(null);
  const [runLaterSelectedAccounts, setRunLaterSelectedAccounts] = useState([]);

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  // Template selection states
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false); // ADDED

  const [loadingStep, setLoadingStep] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingSteps, setLoadingSteps] = useState([]);

  // Preview modal states
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Image modal states
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);

  // AI Chat states
  const [aiChatOpen, setAiChatOpen] = useState(false);

  // Error modal states
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: "", accounts: [], scheduleDate: "" });

  // Monthly post usage count
  const [monthlyPostCount, setMonthlyPostCount] = useState(null);

  // ADDED: Calendar modal state
  const [calendarOpen, setCalendarOpen] = useState(false);

  // UPDATED: Always seed October posts for the current year
  const currentYear = dayjs().year();
  const mk = (d, t) =>
    `${currentYear}-10-${String(d).padStart(2, "0")}T${t}:00`;

  const [scheduledPosts, setScheduledPosts] = useState([
    {
      id: "1",
      title: "Product Launch Announcement",
      content: "Excited to announce our new product launch! 🚀",
      platforms: ["Facebook", "Twitter", "LinkedIn"],
      scheduledDateTime: mk(8, "10:00:00"),
      status: "Scheduled",
    },
    {
      id: "2",
      title: "Weekly Newsletter",
      content: "Don't miss our weekly insights and updates!",
      platforms: ["LinkedIn"],
      scheduledDateTime: mk(10, "14:30:00"),
      status: "Scheduled",
    },
    {
      id: "3",
      title: "Customer Success Story",
      content: "Amazing results from our latest customer partnership",
      platforms: ["Facebook", "Instagram"],
      scheduledDateTime: mk(12, "16:00:00"),
      status: "Scheduled",
    },
    {
      id: "4",
      title: "Feature Spotlight",
      content: "New analytics dashboard ✨",
      platforms: ["Instagram", "LinkedIn"],
      scheduledDateTime: mk(18, "15:45:00"),
      status: "Scheduled",
    },
    {
      id: "5",
      title: "Halloween Promo",
      content: "31% off – spooky deals 🎃",
      platforms: ["Instagram", "Facebook", "Twitter", "LinkedIn"],
      scheduledDateTime: mk(31, "12:00:00"),
      status: "Scheduled",
    },
    {
      id: "6",
      title: "Morning Inspiration",
      content: "New day, new chances.",
      platforms: ["Instagram", "Facebook"],
      scheduledDateTime: mk(25, "08:00:00"),
      status: "Scheduled",
    },
    {
      id: "7",
      title: "Afternoon Update",
      content: "Engagement up 45% 📊",
      platforms: ["LinkedIn", "Twitter"],
      scheduledDateTime: mk(25, "14:30:00"),
      status: "Scheduled",
    },
    {
      id: "8",
      title: "Evening Reflection",
      content: "Small steps → big wins.",
      platforms: ["Instagram"],
      scheduledDateTime: mk(25, "19:00:00"),
      status: "Scheduled",
    },
  ]);

  // ADDED: container for CalendarView Drawer inside Dialog
  const calendarDialogContentRef = useRef(null);
  const [drawerContainerEl, setDrawerContainerEl] = useState(null);
  useEffect(() => {
    if (calendarOpen) setDrawerContainerEl(calendarDialogContentRef.current);
  }, [calendarOpen]);

  // Get current tab values - REMOVED currentHashtag
  const currentPlatforms = tab === 0 ? runNowPlatforms : runLaterPlatforms;
  const currentContentType =
    tab === 0 ? runNowContentType : runLaterContentType;
  const currentMediaFile = tab === 0 ? runNowMediaFile : runLaterMediaFile;
  const currentPostContent =
    tab === 0 ? runNowPostContent : runLaterPostContent;
  const currentSelectedAccounts =
    tab === 0 ? runNowSelectedAccounts : runLaterSelectedAccounts;

  // Filter templates based on search query
  const filteredTemplates = postTemplates.filter(
    (template) =>
      template.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
      template.title.toLowerCase().includes(templateSearchQuery.toLowerCase())
  );

  // Load social media accounts and monthly post count on mount
  useEffect(() => {
    fetchSocialMediaAccounts();
    // Fetch current month's post count for display
    if (allowPerMonthPost !== null) {
      const _months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const _now = new Date();
      const _monthYear = `${_months[_now.getMonth()]}-${_now.getFullYear()}`;
      const _clientId = getAuthTokenPayload()?.clientId;
      fetchMonthlyPostCount(_clientId, _monthYear).then((res) => {
        if (res.success && res.data) setMonthlyPostCount(res.data.postCount);
      });
    }
  }, []);

  // Clear platforms and accounts when content type changes
  useEffect(() => {
    if (tab === 0) {
      setRunNowPlatforms([]);
      setRunNowSelectedAccounts([]);
    } else {
      setRunLaterPlatforms([]);
      setRunLaterSelectedAccounts([]);
    }
  }, [currentContentType, tab]);

  // Function to fetch social media accounts
  const fetchSocialMediaAccounts = async () => {
    setLoadingAccounts(true);
    setAccountsError("");
    const clientId = getAuthTokenPayload()?.clientId;

    try {
      const result = await loadSocialMediaAccounts(clientId);

      if (result.success) {
        if (result.isEmpty) {
          setSocialMediaAccounts([]);
          if (result.isError) {
            setAccountsError(result.message);
          }
        } else {
          setSocialMediaAccounts(result.data.accounts);
        }
        console.log("Social media accounts loaded:", result);
      } else {
        setAccountsError(result.message);
        setSocialMediaAccounts([]);
        console.error("Failed to load social media accounts:", result.message);
      }
    } catch (error) {
      setAccountsError(
        "Failed to load social media accounts. Please try again."
      );
      setSocialMediaAccounts([]);
      console.error("Error loading social media accounts:", error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Handlers with content type change logic
  const handleContentTypeChange = (newContentType) => {
    if (tab === 0) {
      setRunNowContentType(newContentType);
      setRunNowPlatforms([]);
      setRunNowSelectedAccounts([]);
    } else {
      setRunLaterContentType(newContentType);
      setRunLaterPlatforms([]);
      setRunLaterSelectedAccounts([]);
    }
  };

  const handlePlatformChange = (event) => {
    const value = event.target.value;
    const platforms = typeof value === "string" ? value.split(",") : value;

    if (tab === 0) {
      setRunNowPlatforms(platforms);
      setRunNowSelectedAccounts([]);
    } else {
      setRunLaterPlatforms(platforms);
      setRunLaterSelectedAccounts([]);
    }
  };

  const handleAccountChange = (event) => {
    const value = event.target.value;
    const accounts = typeof value === "string" ? value.split(",") : value;

    if (tab === 0) {
      setRunNowSelectedAccounts(accounts);
    } else {
      setRunLaterSelectedAccounts(accounts);
    }
  };

  // Function to remove a platform with complete event isolation
  const removePlatform = (platformToRemove) => {
    const newPlatforms = currentPlatforms.filter(
      (platform) => platform !== platformToRemove
    );

    if (tab === 0) {
      setRunNowPlatforms(newPlatforms);
      setRunNowSelectedAccounts([]);
    } else {
      setRunLaterPlatforms(newPlatforms);
      setRunLaterSelectedAccounts([]);
    }
  };

  // Function to remove a social media account with complete event isolation
  const removeAccount = (accountToRemove) => {
    const newAccounts = currentSelectedAccounts.filter(
      (account) => account !== accountToRemove
    );

    if (tab === 0) {
      setRunNowSelectedAccounts(newAccounts);
    } else {
      setRunLaterSelectedAccounts(newAccounts);
    }
  };

  // Function to convert platform name for API calls
  const convertPlatformForAPI = (platform) => {
    if (platform === "Twitter (X)") return "Twitter";
    return platform;
  };

  // Function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Function to generate unique filename
  const generateUniqueFilename = (originalFile) => {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5); // Format: YYYY-MM-DDTHH-MM-SS
    const randomString = Math.random().toString(36).substring(2, 8); // 6 character random string
    const fileExtension = originalFile.name.split(".").pop();
    const baseName = originalFile.name
      .split(".")
      .slice(0, -1)
      .join(".")
      .replace(/[^a-zA-Z0-9]/g, "_"); // Clean base name

    // Format: basename_YYYY-MM-DDTHH-MM-SS_randomString.extension
    const uniqueFilename = `${baseName}_${timestamp}_${randomString}.${fileExtension}`;

    return uniqueFilename;
  };

  // Media upload handler with unique filename generation
  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    // Get current media files
    const currentFiles = currentMediaFile || [];

    const maxImageSize = 10 * 1024 * 1024; // 10MB
    // UPDATED: LinkedIn video has 4MB limit
    const maxVideoSizeLinkedIn = 4 * 1024 * 1024; // 4MB for LinkedIn
    const maxVideoSize = 50 * 1024 * 1024; // 50MB default
    const maxImages = 10;
    const maxVideos = 5;

    let validFiles = [];
    let errors = [];

    const existingImages = currentFiles.filter((file) =>
      file.type?.startsWith("image/")
    ).length;
    const existingVideos = currentFiles.filter((file) =>
      file.type?.startsWith("video/")
    ).length;

    try {
      for (let file of files) {
        let maxSize = maxImageSize;
        let fileTypeCheck = true;
        let currentCount = 0;
        let maxCount = 0;

        if (currentContentType === "Image") {
          maxSize = maxImageSize;
          fileTypeCheck = file.type.startsWith("image/");
          currentCount =
            existingImages +
            validFiles.filter((f) => f.type?.startsWith("image/")).length;
          maxCount = maxImages;
        } else if (currentContentType === "Video") {
          // UPDATED: Use 4MB limit for LinkedIn videos
          maxSize = maxVideoSizeLinkedIn;
          fileTypeCheck = file.type.startsWith("video/");
          currentCount =
            existingVideos +
            validFiles.filter((f) => f.type?.startsWith("video/")).length;
          maxCount = maxVideos;
        }

        if (!fileTypeCheck) {
          errors.push(
            `${
              file.name
            }: Please select a valid ${currentContentType.toLowerCase()} file`
          );
          continue;
        }

        if (currentCount >= maxCount) {
          errors.push(
            `Maximum ${maxCount} ${currentContentType.toLowerCase()}${
              maxCount > 1 ? "s" : ""
            } allowed`
          );
          break;
        }

        if (file.size > maxSize) {
          const maxSizeMB = maxSize / 1024 / 1024;
          const sizeNote = currentContentType === "Video" ? " for LinkedIn" : "";
          errors.push(
            `${file.name}: File size too large. Maximum ${maxSizeMB}MB allowed${sizeNote}`
          );
          continue;
        }

        // FIXED: For Run Later tab, handle S3 upload properly
        if (tab === 1) {
          try {
            console.log(`Uploading ${file.name} to S3...`);

            // Get presigned URL
            const presignedData = await getPresignedUrl(file.name, file.type);
            console.log("Presigned URL data:", presignedData);

            if (!presignedData?.data?.uploadAPIUrl) {
              throw new Error("Failed to get upload URL from server");
            }

            // Upload to S3
            await uploadFileToS3(presignedData.data.uploadAPIUrl, file);
            console.log(
              `✓ ${file.name} uploaded to S3:`,
              presignedData.data.fileUrl
            );

            // FIXED: Extend the actual File object instead of creating plain object
            // This preserves File methods like createObjectURL
            file.s3Url = presignedData.data.fileUrl;
            file.s3Key = presignedData.data.key;
            file.originalName = file.name;
            file.fileName = file.name;

            validFiles.push(file);
            console.log("Enhanced file with S3 metadata:", {
              name: file.name,
              s3Url: file.s3Url,
              s3Key: file.s3Key,
              type: file.type,
              size: file.size,
            });
          } catch (error) {
            errors.push(`Failed to upload ${file.name} to storage`);
            console.error("S3 upload error for", file.name, ":", error);
            continue;
          }
        } else {
          // Run Now - original behavior
          const uniqueFilename = generateUniqueFilename(file);
          const fileWithUniqueName = new File([file], uniqueFilename, {
            type: file.type,
            lastModified: file.lastModified,
          });
          fileWithUniqueName.originalName = file.name;
          fileWithUniqueName.uniqueName = uniqueFilename;
          validFiles.push(fileWithUniqueName);
        }
      }

      // Show errors if any
      if (errors.length > 0) {
        setSnackbarMessage(errors.join(". "));
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }

      if (validFiles.length > 0) {
        const isInstagramSelected = currentPlatforms.includes("Instagram");
        const hasImages = validFiles.some((file) =>
          file.type?.startsWith("image/")
        );

        if (
          isInstagramSelected &&
          hasImages &&
          currentContentType === "Image"
        ) {
          // Filter only image files for cropping
          const imageFiles = validFiles.filter((file) =>
            file.type?.startsWith("image/")
          );
          const nonImageFiles = validFiles.filter(
            (file) => !file.type?.startsWith("image/")
          );

          // Add non-image files immediately
          if (nonImageFiles.length > 0) {
            const newFiles = [...currentFiles, ...nonImageFiles];
            if (tab === 0) {
              setRunNowMediaFile(newFiles);
            } else {
              setRunLaterMediaFile(newFiles);
            }
          }

          // Start Instagram cropping process for images
          setPendingImages(imageFiles);
          setCurrentCroppingIndex(0);
          setCurrentCroppingImage(imageFiles[0]);
          setInstagramCropperOpen(true);

          setSnackbarMessage(
            `${imageFiles.length} image(s) need Instagram formatting. Please crop each image.`
          );
          setSnackbarSeverity("info");
          setOpenSnackbar(true);
        } else {
          // No Instagram cropping needed, add files directly
          const newFiles = [...currentFiles, ...validFiles];

          if (tab === 0) {
            setRunNowMediaFile(newFiles);
          } else {
            setRunLaterMediaFile(newFiles);
          }

          setSnackbarMessage(
            `${validFiles.length} ${currentContentType.toLowerCase()}${
              validFiles.length > 1 ? "s" : ""
            } uploaded successfully!`
          );
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      setSnackbarMessage("Failed to process uploads. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // FIXED: Update Instagram cropper save handler to preserve S3 metadata
  const handleInstagramCropSave = (croppedImage) => {
    const currentFiles = currentMediaFile || [];

    // FIXED: Preserve S3 URL from original image if it exists
    const originalImage = currentCroppingImage;
    if (originalImage?.s3Url) {
      croppedImage.s3Url = originalImage.s3Url;
      croppedImage.s3Key = originalImage.s3Key;
    }

    const newFiles = [...currentFiles, croppedImage];

    if (tab === 0) {
      setRunNowMediaFile(newFiles);
    } else {
      setRunLaterMediaFile(newFiles);
    }

    // Move to next image or finish
    const nextIndex = currentCroppingIndex + 1;
    if (nextIndex < pendingImages.length) {
      setCurrentCroppingIndex(nextIndex);
      setCurrentCroppingImage(pendingImages[nextIndex]);
    } else {
      // All images processed
      setInstagramCropperOpen(false);
      setCurrentCroppingImage(null);
      setCurrentCroppingIndex(null);
      setPendingImages([]);

      setSnackbarMessage("All images processed for Instagram!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    }
  };

  const handleInstagramCropperClose = () => {
    setInstagramCropperOpen(false);
    setCurrentCroppingImage(null);
    setCurrentCroppingIndex(null);
    setPendingImages([]);

    setSnackbarMessage("Instagram cropping cancelled");
    setSnackbarSeverity("info");
    setOpenSnackbar(true);
  };

  // Convert multiple files to base64 with unique filenames
  const convertMultipleFilesToBase64 = async (files) => {
    const fileArray = Array.isArray(files) ? files : [files];
    const base64Files = [];

    for (let file of fileArray) {
      try {
        if (file.fromMarketingAsset && file.url) {
          // Marketing asset: fetch from its S3 URL and convert to base64
          const response = await fetch(file.url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
          });
          base64Files.push({
            name: file.name,
            originalName: file.originalName || file.name,
            type: blob.type || file.type,
            size: blob.size || file.size,
            data: base64,
          });
        } else {
          const base64 = await convertFileToBase64(file);
          base64Files.push({
            name: file.uniqueName || file.name,
            originalName: file.originalName || file.name,
            type: file.type,
            size: file.size,
            data: base64,
          });
        }
      } catch (error) {
        console.error(
          "Error converting file to base64:",
          file.uniqueName || file.name,
          error
        );
        throw new Error(
          `Failed to process file: ${file.originalName || file.name}`
        );
      }
    }

    console.log(
      "Files converted to base64 with unique names:",
      base64Files.map((f) => ({
        original: f.originalName,
        unique: f.name,
        size: `${(f.size / 1024 / 1024).toFixed(2)} MB`,
      }))
    );

    return base64Files;
  };

  // Function to remove individual files
  const removeMediaFile = (indexToRemove) => {
    const currentFiles = currentMediaFile || [];
    const newFiles = currentFiles.filter((_, index) => index !== indexToRemove);

    if (tab === 0) {
      setRunNowMediaFile(newFiles.length > 0 ? newFiles : null);
    } else {
      setRunLaterMediaFile(newFiles.length > 0 ? newFiles : null);
    }

    setSnackbarMessage("File removed successfully");
    setSnackbarSeverity("info");
    setOpenSnackbar(true);
  };

  // Function to remove all files
  const removeAllMediaFiles = () => {
    if (tab === 0) {
      setRunNowMediaFile(null);
    } else {
      setRunLaterMediaFile(null);
    }

    setSnackbarMessage("All files removed");
    setSnackbarSeverity("info");
    setOpenSnackbar(true);
  };

  // Handle assets selected from the Marketing Assets picker
  const handleMarketingAssetSelect = (selectedAssets) => {
    if (!selectedAssets || selectedAssets.length === 0) return;

    const getMimeType = (asset) => {
      const ext = (asset.name || "").split(".").pop().toLowerCase();
      const map = {
        jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
        gif: "image/gif", webp: "image/webp", svg: "image/svg+xml",
        mp4: "video/mp4", mov: "video/quicktime", avi: "video/x-msvideo",
        webm: "video/webm", wmv: "video/x-ms-wmv",
      };
      return map[ext] || (asset.type === "image" ? "image/jpeg" : "video/mp4");
    };

    const currentFiles = currentMediaFile || [];
    const maxCount = currentContentType === "Video" ? 5 : 10;
    const remaining = maxCount - currentFiles.length;

    let toAdd = selectedAssets;
    if (selectedAssets.length > remaining) {
      toAdd = selectedAssets.slice(0, remaining);
      setSnackbarMessage(
        `Only ${remaining > 0 ? remaining : "no more"} asset(s) added — maximum ${maxCount} ${currentContentType?.toLowerCase()}s allowed.`
      );
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      if (remaining === 0) return;
    }

    const pseudoFiles = toAdd.map((asset) => ({
      name: asset.name,
      originalName: asset.name,
      type: getMimeType(asset),
      size: asset.sizeBytes || 0,
      url: asset.url,
      s3Url: asset.url,   // Run Later: already on S3, reuse directly
      s3Path: asset.s3Path,
      fromMarketingAsset: true,
    }));

    const newFiles = [...currentFiles, ...pseudoFiles];
    if (tab === 0) {
      setRunNowMediaFile(newFiles);
    } else {
      setRunLaterMediaFile(newFiles);
    }

    if (toAdd.length === selectedAssets.length) {
      setSnackbarMessage(`${toAdd.length} asset${toAdd.length !== 1 ? "s" : ""} added from Marketing Assets!`);
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    }
  };

  // ADDED: Missing openImageModal function
  const openImageModal = (images, index = 0) => {
    setSelectedImages(images);
    setSelectedImageIndex(index);
    setImageModalOpen(true);
  };

  // Function to check if access tokens are expired now or by scheduled date
  const checkTokenExpiryForSchedule = (selectedAccountIds, scheduleDateTime) => {
    const today = dayjs().startOf("day");
    const scheduleDay = scheduleDateTime
      ? dayjs(scheduleDateTime).startOf("day")
      : null;
    const expiredNow = [];
    const expiredBySchedule = [];

    selectedAccountIds.forEach((accountId) => {
      const account = socialMediaAccounts.find(
        (acc) => acc.accountId === accountId
      );
      const tokenExpiry =
        account?.accessTokenExpiry || account?.tokenExpiry || account?.expiry;

      if (account && tokenExpiry) {
        const expiryDate = dayjs(tokenExpiry).startOf("day");
        if (expiryDate.isBefore(today)) {
          expiredNow.push({
            accountName: account.accountName,
            provider: account.provider,
            expiry: tokenExpiry,
          });
        } else if (scheduleDay && expiryDate.isBefore(scheduleDay)) {
          expiredBySchedule.push({
            accountName: account.accountName,
            provider: account.provider,
            expiry: tokenExpiry,
          });
        }
      }
    });

    return { expiredNow, expiredBySchedule };
  };

  // Handle Run Now submission - REMOVED hashtag validation
  const handleRunNow = async () => {
    const errors = [];

    if (!currentContentType) errors.push("Content Type");
    if (currentPlatforms.length === 0) errors.push("At least one Platform");
    if (currentSelectedAccounts.length === 0)
      errors.push("At least one Social Media Account");
    if (!currentPostContent.trim()) errors.push("Post Content");

    // NEW: Check if media is required but not uploaded
    const isMediaRequired =
      currentContentType && currentContentType !== "Content Only";
    const hasMedia =
      currentMediaFile &&
      (Array.isArray(currentMediaFile) ? currentMediaFile.length > 0 : true);

    if (isMediaRequired && !hasMedia) {
      errors.push(`${currentContentType} file(s)`);
    }

    if (errors.length > 0) {
      setSnackbarMessage(`Please provide: ${errors.join(", ")}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    // Check for expired access tokens
    const { expiredNow } = checkTokenExpiryForSchedule(currentSelectedAccounts, null);
    if (expiredNow.length > 0) {
      setErrorModalData({
        title: "Cannot Post - Expired Tokens",
        accounts: expiredNow,
        scheduleDate: "",
        message: "The following account(s) have expired access tokens and need to be reconnected in Settings before you can post."
      });
      setErrorModalOpen(true);
      return;
    }

    // ── Monthly post limit check ────────────────────────────────
    if (allowPerMonthPost !== null && allowPerMonthPost > 0) {
      const _months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const _now = new Date();
      const _monthYear = `${_months[_now.getMonth()]}-${_now.getFullYear()}`;
      const _clientId = getAuthTokenPayload()?.clientId;
      const _limitResult = await fetchMonthlyPostCount(_clientId, _monthYear);
      if (_limitResult.success && _limitResult.data) {
        const { postCount } = _limitResult.data;
        if (postCount >= allowPerMonthPost) {
          setSnackbarMessage(
            `Monthly post limit reached. You have used all ${allowPerMonthPost} posts allowed for ${_monthYear}. Please wait until next month or upgrade your plan.`
          );
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
          return;
        }
      }
    }
    // ────────────────────────────────────────────────────────────

    // Proceed with preview or submission
    const previewDataObj = {
      type: "runNow",
      platforms: currentPlatforms,
      contentType: currentContentType,
      postContent: currentPostContent,
      mediaFile: currentMediaFile,
      selectedAccounts: currentSelectedAccounts,
    };

    setPreviewData(previewDataObj);
    setPreviewModalOpen(true);
  };

  // Handle Run Later submission - REMOVED hashtag validation
  const handleRunLater = async () => {
    const errors = [];

    if (!currentContentType) errors.push("Content Type");
    if (currentPlatforms.length === 0) errors.push("At least one Platform");
    if (currentSelectedAccounts.length === 0)
      errors.push("At least one Social Media Account");
    if (!currentPostContent.trim()) errors.push("Post Content");
    if (!runLaterDateTime) errors.push("Schedule Date & Time");

    // NEW: Check if media is required but not uploaded
    const isMediaRequired =
      currentContentType && currentContentType !== "Content Only";
    const hasMedia =
      currentMediaFile &&
      (Array.isArray(currentMediaFile) ? currentMediaFile.length > 0 : true);

    if (isMediaRequired && !hasMedia) {
      errors.push(`${currentContentType} file(s)`);
    }

    if (errors.length > 0) {
      setSnackbarMessage(`Please provide: ${errors.join(", ")}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    // Check for expired access tokens
    const { expiredNow, expiredBySchedule } = checkTokenExpiryForSchedule(
      currentSelectedAccounts,
      runLaterDateTime
    );

    if (expiredNow.length > 0) {
      setErrorModalData({
        title: "Cannot Schedule - Expired Tokens",
        accounts: expiredNow,
        scheduleDate: "",
        message: "The following account(s) have expired access tokens and need to be reconnected in Settings before you can schedule this post."
      });
      setErrorModalOpen(true);
      return;
    }

    if (expiredBySchedule.length > 0) {
      const scheduleLabel = dayjs(runLaterDateTime).format("MMMM D, YYYY");
      setErrorModalData({
        title: "Cannot Schedule - Token Expiry Conflict",
        accounts: expiredBySchedule,
        scheduleDate: scheduleLabel,
        message: `The following account(s) will have expired access tokens before your scheduled date of ${scheduleLabel}. Please reconnect these accounts or choose an earlier date.`
      });
      setErrorModalOpen(true);
      return;
    }

    // ── Monthly post limit check ────────────────────────────────
    if (allowPerMonthPost !== null && allowPerMonthPost > 0) {
      const _months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const _now = new Date();
      const _monthYear = `${_months[_now.getMonth()]}-${_now.getFullYear()}`;
      const _clientId = getAuthTokenPayload()?.clientId;
      const _limitResult = await fetchMonthlyPostCount(_clientId, _monthYear);
      if (_limitResult.success && _limitResult.data) {
        const { postCount } = _limitResult.data;
        if (postCount >= allowPerMonthPost) {
          setSnackbarMessage(
            `Monthly post limit reached. You have used all ${allowPerMonthPost} posts allowed for ${_monthYear}. Please wait until next month or upgrade your plan.`
          );
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
          return;
        }
      }
    }
    // ────────────────────────────────────────────────────────────

    setIsSubmitting(true);
    setLoadingProgress(0);
    setLoadingStep("");

    const steps = [
      { step: "Preparing schedule data...", progress: 25 },
      { step: "Processing media files...", progress: 50 },
      { step: "Setting up schedule...", progress: 75 },
      { step: "Schedule confirmed!", progress: 100 },
    ];
    setLoadingSteps(steps);

    try {
      // Step 1
      setLoadingStep(steps[0].step);
      setLoadingProgress(steps[0].progress);
      await new Promise((r) => setTimeout(r, 800));

      // Step 2
      setLoadingStep(steps[1].step);
      setLoadingProgress(steps[1].progress);
      await new Promise((r) => setTimeout(r, 1000));

      // Step 3
      setLoadingStep(steps[2].step);
      setLoadingProgress(steps[2].progress);
      await new Promise((r) => setTimeout(r, 1200));

      // Step 4
      setLoadingStep(steps[3].step);
      setLoadingProgress(steps[3].progress);
      await new Promise((r) => setTimeout(r, 800));

      setSnackbarMessage("Post scheduled successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      // Clear Run Later form
      setRunLaterPostContent("");
      setRunLaterPlatforms([]);
      setRunLaterContentType("");
      setRunLaterMediaFile(null);
      setRunLaterDateTime(null);
      setRunLaterSelectedAccounts([]);
    } catch (error) {
      setSnackbarMessage(
        error.message || "Failed to schedule. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      await new Promise((r) => setTimeout(r, 1000));
      setIsSubmitting(false);
      setLoadingStep("");
      setLoadingProgress(0);
      setLoadingSteps([]);
      setPreviewData(null);
    }
  };

  // Confirm posting function - REMOVED hashtags from payload
  const confirmPosting = async () => {
    // Double-check for expired tokens before posting
    const scheduleDateTime = previewData?.type === "runLater" ? previewData?.dateTime : null;
    const { expiredNow, expiredBySchedule } = checkTokenExpiryForSchedule(
      previewData.selectedAccounts,
      scheduleDateTime
    );

    if (expiredNow.length > 0) {
      setErrorModalData({
        title: previewData.type === 'runNow' ? "Cannot Post - Expired Tokens" : "Cannot Schedule - Expired Tokens",
        accounts: expiredNow,
        scheduleDate: "",
        message: "The following account(s) have expired access tokens and need to be reconnected in Settings."
      });
      setErrorModalOpen(true);
      setPreviewModalOpen(false);
      return;
    }

    if (expiredBySchedule.length > 0 && scheduleDateTime) {
      const scheduleLabel = dayjs(scheduleDateTime).format("MMMM D, YYYY");
      setErrorModalData({
        title: "Cannot Schedule - Token Expiry Conflict",
        accounts: expiredBySchedule,
        scheduleDate: scheduleLabel,
        message: `The following account(s) will have expired access tokens before your scheduled date of ${scheduleLabel}. Please reconnect these accounts or choose an earlier date.`
      });
      setErrorModalOpen(true);
      setPreviewModalOpen(false);
      return;
    }

    // Run Later: call preview API now, then proceed with schedule
    if (previewData?.type === "runLater") {
      try {
        const apiPlatforms = previewData.platforms.map(convertPlatformForAPI);
        const clientId = getAuthTokenPayload()?.clientId;

        // FIXED: Build mediaUrls and mediaFiles arrays properly
        const mediaFiles = previewData.mediaFile
          ? Array.isArray(previewData.mediaFile)
            ? previewData.mediaFile
            : [previewData.mediaFile]
          : [];

        const mediaUrls = mediaFiles.map((file) => file.s3Url || null);

        // Build mediaFiles array with all required properties
        const mediaFilesPayload = mediaFiles.map((file) => ({
          url: file.url || getFileURL(file),
          previewUrl: file.previewUrl || getFileURL(file),
          s3Url: file.s3Url || null,
          fileName: file.fileName || file.name,
          originalName: file.originalName || file.name,
          type: file.type,
          size: file.size,
        }));

        // FIXED: Build accounts array with full account details
        const accountsWithDetails = previewData.selectedAccounts.map(
          (accountId) => {
            const account = socialMediaAccounts.find(
              (acc) => acc.accountId === accountId
            );
            return {
              accountId: account.accountId,
              accountName: account.accountName,
              provider: convertPlatformForAPI(account.provider),
              status: account.status,
            };
          }
        );

        const previewPayload = {
          type: "runLaterPreview",
          platforms: apiPlatforms,
          contentType: previewData.contentType,
          postContent: previewData.postContent,
          accounts: accountsWithDetails,
          selectedAccounts: previewData.selectedAccounts,
          scheduleDateTime: previewData.dateTime.toISOString(),
          mediaFileCount: mediaUrls.length,
          mediaUrls,
          mediaFiles: mediaFilesPayload,
          clientId,
        };

        console.log("Run Later Preview Payload:", previewPayload);

        // Show loader while validating/previewing
        setIsSubmitting(true);
        setLoadingStep("Validating schedule...");
        setLoadingProgress(15);

        // Keep modal visible until click, then close and run API
        setPreviewModalOpen(false);
        await previewRunLaterPost(previewPayload);
      } catch (e) {
        console.error("Preview error:", e);
        setSnackbarMessage(
          e?.message || "Failed to prepare preview. Please try again."
        );
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        // Hide loader on error
        setIsSubmitting(false);
        setLoadingStep("");
        setLoadingProgress(0);
        return; // stop here on error
      }

      await handleRunLater(); // proceed with scheduling flow/loader
      return;
    }

    // Run Now flow (unchanged)
    setPreviewModalOpen(false);
    setIsSubmitting(true);
    setLoadingProgress(0);
    setLoadingStep("");

    const steps = [
      { step: "Preparing post data...", progress: 20 },
      { step: "Processing media files...", progress: 40 },
      { step: "Connecting to platforms...", progress: 60 },
      { step: "Publishing to social media...", progress: 80 },
      { step: "Finalizing post...", progress: 100 },
    ];
    setLoadingSteps(steps);

    try {
      // Step 1: Preparing post data
      setLoadingStep(steps[0].step);
      setLoadingProgress(steps[0].progress);
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (previewData.type === "runNow") {
        // Convert platform names for API
        const apiPlatforms = previewData.platforms.map(convertPlatformForAPI);

        // Prepare the payload with all required data - REMOVED hashtags
        const payload = {
          platforms: apiPlatforms,
          contentType: previewData.contentType,
          postContent: previewData.postContent,
          accounts: previewData.selectedAccounts.map((accountId) => {
            const account = socialMediaAccounts.find(
              (acc) => acc.accountId === accountId
            );
            return {
              accountId: account.accountId,
              accountName: account.accountName,
              provider: convertPlatformForAPI(account.provider),
              username: account.username,
              accessToken: account.accessToken,
              status: account.status,
              groupId: account.groupId,
              groupName: account.groupName,
              connectedBy: account.connectedBy,
              tokenExpiry: account.tokenExpiry,
            };
          }),
          timestamp: new Date().toISOString(),
          clientId: getAuthTokenPayload()?.clientId,
        };

        // Step 2: Processing media files
        setLoadingStep(steps[1].step);
        setLoadingProgress(steps[1].progress);
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (
          previewData.mediaFile &&
          (previewData.contentType === "Image" ||
            previewData.contentType === "Video")
        ) {
          try {
            const files = Array.isArray(previewData.mediaFile)
              ? previewData.mediaFile
              : [previewData.mediaFile];
            const base64Files = await convertMultipleFilesToBase64(files);

            payload.mediaFiles = base64Files;
            payload.mediaFileCount = base64Files.length;

            console.log(
              `${base64Files.length} media file(s) prepared for ${previewData.contentType} upload:`,
              base64Files.map((f) => ({
                name: f.name,
                type: f.type,
                size: `${(f.size / 1024 / 1024).toFixed(2)} MB`,
              }))
            );
          } catch (fileError) {
            console.error("Error processing media files:", fileError);
            throw new Error(
              "Failed to process media files. Please try again with different files."
            );
          }
        }

        // Step 3: Connecting to platforms
        setLoadingStep(steps[2].step);
        setLoadingProgress(steps[2].progress);
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Validate content type requirements
        if (
          (previewData.contentType === "Image" ||
            previewData.contentType === "Video") &&
          !previewData.mediaFile
        ) {
          throw new Error(
            `${
              previewData.contentType
            } content type requires media file(s). Please upload ${previewData.contentType.toLowerCase()} file(s).`
          );
        }

        console.log(
          `Preparing to post ${previewData.contentType} content to social media...`
        );

        // Step 4: Publishing to social media
        setLoadingStep(steps[3].step);
        setLoadingProgress(steps[3].progress);
        await new Promise((resolve) => setTimeout(resolve, 1200));

        // Use the API function from Action/api.js with content type routing
        const result = await postToSocialMedia(payload);

        // Step 5: Finalizing post
        setLoadingStep(steps[4].step);
        setLoadingProgress(steps[4].progress);
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (result.success) {
          // Success - Clear form after successful submission - REMOVED hashtag clearing
          console.log(
            `Post successfully sent via endpoint: ${result.endpoint}`
          );
          setSnackbarMessage(result.message);
          setSnackbarSeverity("success");
          setOpenSnackbar(true);
          // Update the post count display
          setMonthlyPostCount((prev) => (prev !== null ? prev + 1 : 1));

          // Increment monthly post count via API
          try {
            const _months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const _now = new Date();
            const _monthYear = `${_months[_now.getMonth()]}-${_now.getFullYear()}`;
            const _clientId = getAuthTokenPayload()?.clientId;
            const _postBody = {
              postType: "RunNow",
              clientId: _clientId,
              monthYear: _monthYear,
              platforms: previewData.platforms,
              contentType: previewData.contentType,
              postContent: previewData.postContent,
              selectedAccounts: previewData.selectedAccounts,
              accountCount: previewData.selectedAccounts.length,
              mediaFileCount: payload.mediaFileCount || 0,
              accounts: payload.accounts.map((acc) => ({
                accountId: acc.accountId,
                accountName: acc.accountName,
                provider: acc.provider,
                status: acc.status,
              })),
              timestamp: payload.timestamp,
            };
            await incrementMonthlyPostCount(_clientId, _monthYear, _postBody);
          } catch (countErr) {
            console.error("Failed to increment monthly post count:", countErr);
          }

          setRunNowPostContent("");
          setRunNowPlatforms([]);
          setRunNowContentType("");
          setRunNowMediaFile(null);
          setRunNowSelectedAccounts([]);
        } else {
          // API returned error
          throw new Error(result.message);
        }
      } else {
        // For "Run Later" - simulate with steps
        // Step 2: Processing media files
        setLoadingStep(steps[1].step);
        setLoadingProgress(steps[1].progress);
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Step 3: Setting up schedule
        setLoadingStep(steps[2].step);
        setLoadingProgress(steps[2].progress);
        await new Promise((resolve) => setTimeout(resolve, 1200));

        // Step 4: Schedule confirmed
        setLoadingStep(steps[3].step);
        setLoadingProgress(steps[3].progress);
        await new Promise((resolve) => setTimeout(resolve, 800));

        setSnackbarMessage("Post scheduled successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);

        // Clear Run Later form
        setRunLaterPostContent("");
        setRunLaterPlatforms([]);
        setRunLaterContentType("");
        setRunLaterMediaFile(null);
        setRunLaterDateTime(null);
        setRunLaterSelectedAccounts([]);
      }
    } catch (error) {
      console.error("Error posting to social media:", error);

      // Display the error message
      setSnackbarMessage(
        error.message || "Failed to send post. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      // ADDED: Delay before hiding loader for better UX
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsSubmitting(false);
      setLoadingStep("");
      setLoadingProgress(0);
      setLoadingSteps([]);
      setPreviewData(null);
    }
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewData(null);
  };

  // Handler for closing snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  // Template handlers
  const openTemplateModal = () => {
    setTemplateModalOpen(true);
  };

  const closeTemplateModal = () => {
    setTemplateModalOpen(false);
    setSelectedTemplate(null);
    setTemplateSearchQuery("");
  };

  const selectTemplate = (template) => {
    if (tab === 0) {
      setRunNowPostContent(template.content);
    } else {
      setRunLaterPostContent(template.content);
    }
    setTemplateModalOpen(false);
  };

  // AI Chat handlers
  const openAIChat = () => {
    setAiChatOpen(true);
  };

  const closeAIChat = () => {
    setAiChatOpen(false);
  };

  const handleAIContentGenerated = (content) => {
    // Parse AI generated content and fill form fields
    try {
      // Clean up the content by removing markdown formatting
      const postContent = content
        .replace(/\*\*/g, "")
        .replace(/^#+\s*/gm, "")
        .replace(/#{2,}.*$/gm, "")
        .trim();

      if (tab === 0) {
        if (postContent) setRunNowPostContent(postContent);
      } else {
        if (postContent) setRunLaterPostContent(postContent);
      }

      setAiChatOpen(false);
      setSnackbarMessage(
        "AI content applied successfully! Review and edit as needed."
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Error parsing AI content:", error);

      if (tab === 0) {
        setRunNowPostContent(content);
      } else {
        setRunLaterPostContent(content);
      }

      setAiChatOpen(false);
      setSnackbarMessage(
        "AI content added to post content. Please review and organize as needed."
      );
      setSnackbarSeverity("info");
      setOpenSnackbar(true);
    }
  };

  // Reset form handler - REMOVED hashtag clearing
  const handleResetForm = () => {
    if (tab === 0) {
      setRunNowPlatforms([]);
      setRunNowContentType("");
      setRunNowMediaFile(null);
      setRunNowPostContent("");
      setRunNowSelectedAccounts([]);
    } else {
      setRunLaterPlatforms([]);
      setRunLaterContentType("");
      setRunLaterMediaFile(null);
      setRunLaterPostContent("");
      setRunLaterDateTime(null);
      setRunLaterSelectedAccounts([]);
    }

    setSnackbarMessage("Form has been reset successfully!");
    setSnackbarSeverity("info");
    setOpenSnackbar(true);
  };

  // Filter accounts based on selected platforms
  const getAvailableAccounts = () => {
    if (currentPlatforms.length === 0) return [];

    const apiPlatforms = currentPlatforms.map(convertPlatformForAPI);

    return socialMediaAccounts.filter((account) => {
      return account.status === 'Active' && apiPlatforms.some(
        (platform) => account.provider.toLowerCase() === platform.toLowerCase()
      );
    });
  };

  const availablePlatforms = getAvailablePlatforms(currentContentType);
  const availableAccounts = getAvailableAccounts();

  // ADDED: Calendar handlers
  const openCalendar = () => {
    setCalendarOpen(true);
  };

  const closeCalendar = () => {
    setCalendarOpen(false);
  };

  const handleCalendarDateTimeSelect = (dateTime) => {
    // ensure dayjs object for formatter usage elsewhere
    setRunLaterDateTime(dayjs(dateTime));
    setCalendarOpen(false);
  };

  // Handle Run Later preview (validate only, no API here)
  const handleOpenRunLaterPreview = async () => {
    const errors = [];

    // Basic validations
    if (!currentPostContent.trim()) errors.push("Post Content");
    if (currentPlatforms.length === 0) errors.push("At least one Platform");
    if (!currentContentType) errors.push("Content Type");
    if (currentSelectedAccounts.length === 0)
      errors.push("At least one Social Media Account");
    if (!runLaterDateTime) errors.push("Date & Time");

    // ADDED: Media file validation based on content type
    if (currentContentType === "Image" || currentContentType === "Video") {
      if (
        !currentMediaFile ||
        (Array.isArray(currentMediaFile) && currentMediaFile.length === 0)
      ) {
        errors.push(
          `${currentContentType} file${
            currentContentType === "Image" ? "(s)" : ""
          }`
        );
      }
    }

    if (errors.length > 0) {
      setSnackbarMessage(`Please provide: ${errors.join(", ")}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    // ── Monthly post limit check ────────────────────────────────
    if (allowPerMonthPost !== null && allowPerMonthPost > 0) {
      const _months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const _now = new Date();
      const _monthYear = `${_months[_now.getMonth()]}-${_now.getFullYear()}`;
      const _clientId = getAuthTokenPayload()?.clientId;
      const _limitResult = await fetchMonthlyPostCount(_clientId, _monthYear);
      if (_limitResult.success && _limitResult.data) {
        const { postCount } = _limitResult.data;
        if (postCount >= allowPerMonthPost) {
          setSnackbarMessage(
            `Monthly post limit reached. You have used all ${allowPerMonthPost} posts allowed for ${_monthYear}. Please wait until next month or upgrade your plan.`
          );
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
          return;
        }
      }
    }
    // ────────────────────────────────────────────────────────────

    const previewDataObj = {
      type: "runLater",
      platforms: currentPlatforms,
      contentType: currentContentType,
      postContent: currentPostContent,
      mediaFile: currentMediaFile,
      selectedAccounts: currentSelectedAccounts,
      dateTime: runLaterDateTime,
    };

    // Open preview only
    setPreviewData(previewDataObj);
    setPreviewModalOpen(true);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Instagram Image Cropper */}
      <InstagramImageCropper
        open={instagramCropperOpen}
        onClose={handleInstagramCropperClose}
        image={currentCroppingImage}
        onSave={handleInstagramCropSave}
        fileName={
          currentCroppingImage?.uniqueName || currentCroppingImage?.name
        }
      />

      {/* Beautiful Full-Screen Loading Overlay */}
      <Backdrop
        open={isSubmitting}
        sx={{
          color: "#fff",
          zIndex: 9999,
          bgcolor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxWidth: 500,
            width: "90%",
            p: 4,
          }}
        >
          {/* Main Loading Animation */}
          <Fade in={isSubmitting} timeout={500}>
            <Paper
              elevation={24}
              sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                width: "100%",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Animated Icons */}
              <Box sx={{ mb: 3, position: "relative", height: 80 }}>
                <Zoom
                  in={isSubmitting}
                  timeout={800}
                  style={{ transitionDelay: "200ms" }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      bgcolor: "#4B5AB6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": {
                          transform: "translateX(-50%) scale(1)",
                          boxShadow: "0 0 0 0 rgba(75, 90, 182, 0.7)",
                        },
                        "70%": {
                          transform: "translateX(-50%) scale(1.05)",
                          boxShadow: "0 0 0 10px rgba(75, 90, 182, 0)",
                        },
                        "100%": {
                          transform: "translateX(-50%) scale(1)",
                          boxShadow: "0 0 0 0 rgba(75, 90, 182, 0)",
                        },
                      },
                    }}
                  >
                    {previewData?.type === "runNow" ? (
                      loadingProgress >= 80 ? (
                        <ShareIcon sx={{ fontSize: 28 }} />
                      ) : loadingProgress >= 40 ? (
                        <UploadIcon sx={{ fontSize: 28 }} />
                      ) : (
                        <SendIcon sx={{ fontSize: 28 }} />
                      )
                    ) : (
                      <ScheduleIcon sx={{ fontSize: 28 }} />
                    )}
                  </Box>
                </Zoom>

                {/* Success Checkmark Animation */}
                {loadingProgress >= 100 && (
                  <Zoom
                    in={true}
                    timeout={600}
                    style={{ transitionDelay: "500ms" }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -5,
                        right: "calc(50% - 45px)",
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        bgcolor: "#10b981",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        animation: "bounce 1s ease-in-out",
                        "@keyframes bounce": {
                          "0%, 20%, 53%, 80%, 100%": {
                            transform: "translate3d(0,0,0)",
                          },
                          "40%, 43%": {
                            transform: "translate3d(0,-15px,0)",
                          },
                          "70%": {
                            transform: "translate3d(0,-7px,0)",
                          },
                          "90%": {
                            transform: "translate3d(0,-2px,0)",
                          },
                        },
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </Zoom>
                )}
              </Box>

              {/* Main Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  mb: 1,
                  animation: "fadeInUp 0.8s ease-out",
                  "@keyframes fadeInUp": {
                    from: {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                {previewData?.type === "runNow"
                  ? loadingProgress >= 100
                    ? "🎉 Post Published Successfully!"
                    : "📤 Publishing Your Post..."
                  : loadingProgress >= 100
                  ? "⏰ Schedule Created Successfully!"
                  : "📅 Scheduling Your Post..."}
              </Typography>

              {/* Current Step */}
              <Typography
                variant="body1"
                sx={{
                  color: "#4B5AB6",
                  fontWeight: 600,
                  mb: 3,
                  minHeight: 24,
                  animation: "fadeIn 0.5s ease-in-out",
                  "@keyframes fadeIn": {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                {loadingStep}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ width: "100%", mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={loadingProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "#e2e8f0",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      background:
                        "linear-gradient(90deg, #4B5AB6 0%, #10b981 100%)",
                      transition: "transform 0.8s ease-in-out",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontWeight: 600,
                    mt: 1,
                    display: "block",
                    textAlign: "center",
                  }}
                >
                  {loadingProgress}% Complete
                </Typography>
              </Box>

              {/* Platform and Account Info */}
              <Box
                sx={{
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  p: 2,
                  border: "1px solid #e2e8f0",
                }}
              >
                {previewData && (
                  <>
                    {/* Platforms */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#64748b", fontWeight: 600 }}
                      >
                        Publishing to:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mt: 0.5,
                        }}
                      >
                        {previewData.platforms.map((platform, index) => (
                          <Chip
                            key={index}
                            label={platform}
                            size="small"
                            sx={{
                              bgcolor: "#e0e7ff",
                              color: "#4B5AB6",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Account Count */}
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      📱 {previewData.selectedAccounts.length} account
                      {previewData.selectedAccounts.length !== 1
                        ? "s"
                        : ""}{" "}
                      selected
                      {previewData.mediaFile && (
                        <>
                          {" "}
                          • 📎{" "}
                          {Array.isArray(previewData.mediaFile)
                            ? previewData.mediaFile.length
                            : 1}{" "}
                          media file
                          {(Array.isArray(previewData.mediaFile)
                            ? previewData.mediaFile.length
                            : 1) !== 1
                            ? "s"
                            : ""}
                        </>
                      )}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Floating Particles Animation */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  pointerEvents: "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: "-50%",
                    left: "-50%",
                    width: "200%",
                    height: "200%",
                    background: `
                      radial-gradient(circle at 20% 80%, rgba(75, 90, 182, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)
                    `,
                    animation: "float 6s ease-in-out infinite",
                  },
                  "@keyframes float": {
                    "0%, 100%": {
                      transform: "translate(-50%, -50%) rotate(0deg)",
                    },
                    "33%": {
                      transform: "translate(-48%, -52%) rotate(120deg)",
                    },
                    "66%": {
                      transform: "translate(-52%, -48%) rotate(240deg)",
                    },
                  },
                }}
              />
            </Paper>
          </Fade>
        </Box>
      </Backdrop>

      <Box sx={{ py: 5, bgcolor: "#f8fafc", minHeight: "100vh" }}>

        {/* ── Fixed floating AI button – always visible ── */}
        <Tooltip
          title={!isBusinessPlan ? "AI Assistant · Business plan required" : "Open AI Content Assistant"}
          arrow
          placement="left"
        >
          <Box
            onClick={isBusinessPlan ? openAIChat : undefined}
            sx={{
              position: "fixed",
              bottom: 32,
              right: 28,
              zIndex: 1300,
              width: 58,
              height: 58,
              borderRadius: "50%",
              cursor: isBusinessPlan ? "pointer" : "not-allowed",
              background: "transparent",
              boxShadow: isBusinessPlan
                ? "0 4px 20px rgba(99,102,241,0.45), 0 2px 10px rgba(34,211,238,0.3)"
                : "0 2px 10px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.18s ease, box-shadow 0.18s ease",
              animation: isBusinessPlan ? "fabPulse 3s ease-in-out infinite" : "none",
              "@keyframes fabPulse": {
                "0%, 100%": { boxShadow: "0 4px 20px rgba(99,102,241,0.45), 0 2px 10px rgba(34,211,238,0.3)" },
                "50%":       { boxShadow: "0 6px 30px rgba(99,102,241,0.65), 0 4px 18px rgba(52,211,153,0.45)" },
              },
              "&:hover": isBusinessPlan ? {
                transform: "scale(1.1)",
                boxShadow: "0 8px 32px rgba(99,102,241,0.55), 0 4px 16px rgba(34,211,238,0.4)",
              } : {},
            }}
          >
            {isBusinessPlan
              ? <OruclicksAIIcon size={58} />
              : <Typography sx={{ fontSize: "1.2rem", lineHeight: 1 }}>🔒</Typography>
            }
          </Box>
        </Tooltip>

        <Container maxWidth="md">
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#4B5AB6", mb: 0.5 }}>
              Social Media Post
            </Typography>
            <Typography variant="body1" sx={{ color: "#64738b", fontWeight: 400 }}>
              Create and share posts for multiple platforms with media and content
            </Typography>
          </Box>

          {/* Monthly Post Quota Banner */}
          {allowPerMonthPost !== null && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mb: 2.5,
                flexWrap: "wrap",
              }}
            >
              {/* Current month pill */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: "#eef2ff",
                  border: "1px solid #c7d2fe",
                  borderRadius: "20px",
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 13, color: "#4B5AB6" }} />
                <Typography sx={{ fontSize: "0.73rem", fontWeight: 600, color: "#3730a3", lineHeight: 1 }}>
                  {dayjs().format("MMMM YYYY")}
                </Typography>
              </Box>

              {/* Allowed / used posts pill */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: "20px",
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "0.73rem", fontWeight: 700, color: "#15803d", lineHeight: 1 }}>
                  ✦{" "}
                  {monthlyPostCount !== null ? (
                    <>
                      <span style={{ color: "#15803d" }}>{monthlyPostCount}</span>
                      <span style={{ color: "#6b7280", fontWeight: 500 }}> / {allowPerMonthPost}</span>
                      <span style={{ color: "#6b7280", fontWeight: 500 }}> posts used this month</span>
                    </>
                  ) : (
                    `${allowPerMonthPost} posts allowed this month`
                  )}
                </Typography>
              </Box>

              {/* Run Later counting note - only shown when Run Later is enabled */}
              {canRunLater && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: "20px",
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 500, color: "#c2410c", lineHeight: 1 }}>
                    Run Now + Run Later both count toward limit
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Accounts Error Alert */}
          {accountsError && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <Button
                  size="small"
                  onClick={fetchSocialMediaAccounts}
                  disabled={loadingAccounts}
                  startIcon={
                    loadingAccounts ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                >
                  Retry
                </Button>
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Error Loading Accounts
              </Typography>
              <Typography variant="body2">{accountsError}</Typography>
            </Alert>
          )}

          <Paper
            elevation={8}
            sx={{
              borderRadius: 4,
              boxShadow: "0 8px 32px rgba(75,90,182,0.16)",
              p: 0,
              maxWidth: 700,
              mx: "auto",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{
                background: "#f8fafc",
                color: "#4B5AB6",
                "& .Mui-selected": { color: "#4B5AB6 !important" },
                "& .MuiTabs-indicator": { backgroundColor: "#4B5AB6" },
              }}
            >
              <Tab
                label="Run Now"
                icon={<SendIcon />}
                iconPosition="start"
                sx={{ fontWeight: 700, fontSize: "1rem" }}
              />
              {canRunLater && (
                <Tab
                  label="Run Later"
                  icon={<ScheduleIcon />}
                  iconPosition="start"
                  sx={{ fontWeight: 700, fontSize: "1rem" }}
                />
              )}
            </Tabs>

            <Divider />

            <Box sx={{ p: 4, background: "#fff" }}>
              {tab === 0 ? (
                <RunNow
                  platforms={runNowPlatforms}
                  contentType={runNowContentType}
                  mediaFile={runNowMediaFile}
                  postContent={runNowPostContent}
                  selectedAccounts={runNowSelectedAccounts}
                  socialMediaAccounts={socialMediaAccounts}
                  loadingAccounts={loadingAccounts}
                  onContentTypeChange={handleContentTypeChange}
                  onPlatformChange={handlePlatformChange}
                  onAccountChange={handleAccountChange}
                  onPostContentChange={(value) => setRunNowPostContent(value)}
                  onMediaUpload={handleMediaUpload}
                  onRemovePlatform={removePlatform}
                  onRemoveAccount={removeAccount}
                  onRemoveMediaFile={removeMediaFile}
                  onRemoveAllMediaFiles={removeAllMediaFiles}
                  getAvailablePlatforms={getAvailablePlatforms}
                  getAvailableAccounts={getAvailableAccounts}
                  onOpenAIChat={openAIChat}
                  isBusinessPlan={isBusinessPlan}
                  onOpenTemplate={openTemplateModal}
                  onMarketingAssetSelect={handleMarketingAssetSelect}
                />
              ) : (
                <RunLater
                  platforms={runLaterPlatforms}
                  contentType={runLaterContentType}
                  mediaFile={runLaterMediaFile}
                  postContent={runLaterPostContent}
                  selectedAccounts={runLaterSelectedAccounts}
                  socialMediaAccounts={socialMediaAccounts}
                  loadingAccounts={loadingAccounts}
                  dateTime={runLaterDateTime}
                  onContentTypeChange={handleContentTypeChange}
                  onPlatformChange={handlePlatformChange}
                  onAccountChange={handleAccountChange}
                  onPostContentChange={(value) => setRunLaterPostContent(value)}
                  onMediaUpload={handleMediaUpload}
                  onRemovePlatform={removePlatform}
                  onRemoveAccount={removeAccount}
                  onRemoveMediaFile={removeMediaFile}
                  onRemoveAllMediaFiles={removeAllMediaFiles}
                  onOpenCalendar={openCalendar}
                  getAvailablePlatforms={getAvailablePlatforms}
                  getAvailableAccounts={getAvailableAccounts}
                  onOpenAIChat={openAIChat}
                  isBusinessPlan={isBusinessPlan}
                  onOpenTemplate={openTemplateModal}
                  onMarketingAssetSelect={handleMarketingAssetSelect}
                />
              )}


            </Box>

            <Divider />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 3,
                background: "#f8fafc",
              }}
            >
              {/* Left side - Reset button */}
              <Button
                variant="text"
                startIcon={<ResetIcon />}
                onClick={handleResetForm}
                sx={{
                  color: "#64748b",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                    color: "#ef4444",
                  },
                }}
              >
                Reset All
              </Button>

              {/* Right side - Post/Schedule button */}
              <Button
                variant="contained"
                startIcon={
                  isSubmitting || isPreviewLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : tab === 0 ? (
                    <SendIcon />
                  ) : (
                    <ScheduleIcon />
                  )
                }
                disabled={isSubmitting || isPreviewLoading} // ADDED
                sx={{
                  background:
                    isSubmitting || isPreviewLoading
                      ? "#94a3b8"
                      : "linear-gradient(90deg, #4B5AB6 0%, #6366f1 100%)",
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  boxShadow: "0 2px 8px rgba(75,90,182,0.12)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    background:
                      isSubmitting || isPreviewLoading
                        ? "#94a3b8"
                        : "linear-gradient(90deg, #6366f1 0%, #4B5AB6 100%)",
                    transform:
                      isSubmitting || isPreviewLoading ? "none" : "scale(1.04)",
                  },
                  "&:disabled": { color: "#fff", cursor: "not-allowed" },
                }}
                onClick={tab === 0 ? handleRunNow : handleOpenRunLaterPreview}
              >
                {isSubmitting
                  ? previewData?.type === "runNow"
                    ? "Posting..."
                    : "Scheduling..."
                  : tab === 0
                  ? "Post Now"
                  : isPreviewLoading
                  ? "Preparing Preview..."
                  : "Schedule"}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Template Selection Modal */}
      <TemplateModal
        open={templateModalOpen}
        onClose={closeTemplateModal}
        onSelect={selectTemplate}
        searchQuery={templateSearchQuery}
        onSearchChange={setTemplateSearchQuery}
        selectedTemplate={selectedTemplate}
        onClearSelection={() => setSelectedTemplate(null)}
      />

      {/* UPDATED: Preview Confirmation Modal with account information */}
      {/* REPLACED with reusable component */}
      <PreviewPostDialog
        open={previewModalOpen}
        onClose={closePreviewModal}
        onConfirm={confirmPosting}
        isSubmitting={isSubmitting}
        previewData={previewData}
        socialMediaAccounts={socialMediaAccounts}
        onOpenImageModal={openImageModal}
      />

      {/* ADDED: Image Lightbox Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "rgba(0,0,0,0.9)",
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "white",
            pb: 1,
          }}
        >
          Image {selectedImageIndex + 1} of {selectedImages.length}
          <IconButton onClick={() => setImageModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, textAlign: "center" }}>
          {selectedImages[selectedImageIndex] && (
            <Box sx={{ position: "relative" }}>
              <img
                src={getFileURL(selectedImages[selectedImageIndex])}
                alt={`Full size preview ${selectedImageIndex + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  width: "auto",
                  height: "auto",
                  borderRadius: "8px",
                  objectFit: "contain",
                }}
              />

              {/* Navigation arrows for multiple images */}
              {selectedImages.length > 1 && (
                <>
                  <IconButton
                    onClick={() =>
                      setSelectedImageIndex((prev) =>
                        prev === 0 ? selectedImages.length - 1 : prev - 1
                      )
                    }
                    sx={{
                      position: "absolute",
                      left: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    ←
                  </IconButton>

                  <IconButton
                    onClick={() =>
                      setSelectedImageIndex((prev) =>
                        prev === selectedImages.length - 1 ? 0 : prev + 1
                      )
                    }
                    sx={{
                      position: "absolute",
                      right: 16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    →
                  </IconButton>
                </>
              )}
            </Box>
          )}

          {/* Image details */}
          {selectedImages[selectedImageIndex] && (
            <Box sx={{ mt: 2, color: "white", textAlign: "center" }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {selectedImages[selectedImageIndex].originalName}
              </Typography>
              {selectedImages[selectedImageIndex].uniqueName && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(34, 197, 94, 0.8)",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Unique: {selectedImages[selectedImageIndex].uniqueName}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {(
                  selectedImages[selectedImageIndex].size /
                  1024 /
                  1024
                ).toFixed(2)}{" "}
                MB •{selectedImages[selectedImageIndex].type}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Calendar View Modal - FIXED: wrap CalendarView in Dialog */}
      <Dialog
        open={calendarOpen}
        onClose={closeCalendar}
        fullWidth
        maxWidth="xl"
        keepMounted={false} // Change to false to fully unmount on close
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#111827",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          📅 Schedule Your Post
          <IconButton onClick={closeCalendar} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, bgcolor: "#f8fafc" }}
          ref={calendarDialogContentRef}
        >
          <CalendarView
            open={calendarOpen}
            onClose={closeCalendar}
            onDateTimeSelect={handleCalendarDateTimeSelect}
            selectedDateTime={runLaterDateTime}
            title="Schedule"
            drawerContainer={drawerContainerEl}
          />
        </DialogContent>
      </Dialog>

      {/* AI Chat Component */}
      <AIChat
        open={aiChatOpen}
        onClose={closeAIChat}
        onContentGenerated={handleAIContentGenerated}
        title="✨ AI Content Creator"
        placeholder="Tell me what kind of social media post you want to create..."
        initialPrompt=""
      />

      {/* Error Modal for Token Expiry */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "3px solid #fca5a5",
            boxShadow: "0 8px 32px rgba(220, 38, 38, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
            color: "#991b1b",
            fontWeight: 700,
            fontSize: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "2px solid #fca5a5",
            pb: 2,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 40, color: "#dc2626" }} />
          {errorModalData?.title || "Error"}
          <IconButton
            onClick={() => setErrorModalOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#991b1b",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Alert
            severity="error"
            icon={<WarningAmberIcon fontSize="large" />}
            sx={{
              mb: 3,
              fontSize: "0.95rem",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            {errorModalData?.message}
          </Alert>

          <Box
            sx={{
              bgcolor: "#fef2f2",
              border: "1px solid #fca5a5",
              borderRadius: 2,
              p: 2,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 28, color: "#dc2626" }} />
            <Box>
              <Typography variant="body2" sx={{ color: "#991b1b", fontWeight: 600 }}>
                Scheduled Date
              </Typography>
              <Typography variant="h6" sx={{ color: "#dc2626", fontWeight: 700 }}>
                {errorModalData?.scheduleDate || "Not specified"}
              </Typography>
            </Box>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "#374151" }}>
            Affected Accounts:
          </Typography>

          <List sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 0 }}>
            {errorModalData?.accounts?.map((account, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    py: 2,
                    "&:hover": { bgcolor: "#f3f4f6" },
                  }}
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#fee2e2",
                        color: "#dc2626",
                        fontWeight: 700,
                        fontSize: "1.2rem",
                      }}
                    >
                      {account.provider?.charAt(0)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#111827" }}>
                        {account.accountName}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b7280",
                            display: "inline-block",
                            bgcolor: "#e5e7eb",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            mr: 1,
                          }}
                        >
                          {account.provider}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#dc2626",
                            fontWeight: 600,
                            display: "inline-block",
                            bgcolor: "#fee2e2",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          Expires: {new Date(account.expiry).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < (errorModalData?.accounts?.length || 0) - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              💡 What to do next:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              1. Go to <strong>Settings</strong> → <strong>Social Media Accounts</strong>
              <br />
              2. Reconnect the affected account(s)
              <br />
              3. Return here and try scheduling again
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            justifyContent: "center",
            borderTop: "1px solid #e5e7eb",
            pt: 2,
          }}
        >
          <Button
            onClick={() => setErrorModalOpen(false)}
            variant="contained"
            sx={{
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
              fontWeight: 600,
              minWidth: 120,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default RunPost;
